import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { Button, Divider } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUpRightFromSquare,
    faCheck,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import SITrendline from "@/components/field/insights/SITrendline";
import DateInput from "@/components/inputs/DateInput";
import StatusTimeline from "@/components/home/StatusTimeline";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { useTranslation } from "react-i18next";
import { FieldInsights } from "@/types/fieldInsights";
import SoilWaterContent from "@/components/field/insights/SoilWaterContent";
import { EnrollmentTier } from "@/lib/utils/enrollment";
import hydroFlowHorizontal from "@/assets/images/HydroFlow_Horizontal_TM.png";
import { parseDate } from "@internationalized/date";
import useAuth from "@/lib/auth/useAuth";

function SIPreview({ selectedField }: { selectedField: FieldInsights }) {
    const { t } = useTranslation("home-field");
    const { isConsultantOrSentinel } = useAuth();
    const { notificationsV2Enabled } = useFeatureFlag();
    const f = useForm({
        defaultValues: {
            startDate: getTodayWithTimeTravel().subtract(1, "month").toDate(),
            endDate: getTodayWithTimeTravel().toDate(),
        },
    });

    const [startDate, endDate] = f.watch(["startDate", "endDate"]);
    const lastTwoMonths = useMemo(() => {
        if (!startDate || !endDate) return false;

        return dayjs(startDate)
            .add(2, "months")
            .isSame(getTodayWithTimeTravel(), "days");
    }, [endDate, startDate]);

    const lastTwoWeeks = useMemo(() => {
        if (!startDate || !endDate) return false;

        return dayjs(startDate)
            .add(2, "weeks")
            .isSame(getTodayWithTimeTravel(), "days");
    }, [endDate, startDate]);

    const fieldHasInsights =
        selectedField.active_enrollment?.enrollment_tier !==
            EnrollmentTier.Irrigation &&
        selectedField.active_enrollment?.enrollment_tier !==
            EnrollmentTier.Monitoring &&
        (isConsultantOrSentinel ||
            selectedField.active_enrollment?.enrollment_tier !==
                EnrollmentTier.Preview);
    const { irrigationEnabled } = useFeatureFlag();
    const fieldHasIrrigation = useMemo(() => {
        if (!selectedField.active_enrollment) return false;
        return (
            [EnrollmentTier.Irrigation, EnrollmentTier.Complete].includes(
                selectedField.active_enrollment
                    .enrollment_tier as EnrollmentTier,
            ) && irrigationEnabled
        );
    }, [selectedField.active_enrollment, irrigationEnabled]);

    return (
        <div className="flex flex-col gap-2">
            <div>
                <Button
                    as={Link}
                    to={`/dashboard/fields/$fieldId/insights/ntime/?cameFrom=home`}
                    // @ts-expect-error - Button -> Link
                    params={{ fieldId: selectedField.id.toString() }}
                    className="text-xl font-semibold"
                    size="lg"
                    variant="light"
                    target="_blank"
                    endContent={
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    }
                >
                    {selectedField.name}
                </Button>
            </div>
            <FormProvider {...f}>
                <div className="flex flex-row self-end justify-between w-full  gap-2 items-center">
                    <div className="font-semibold text-sm">
                        {t("date_range")}
                    </div>
                    <div className="flex flex-row gap-2">
                        <Button
                            color={lastTwoMonths ? "success" : "default"}
                            variant="flat"
                            size="sm"
                            onPress={() => {
                                f.setValue(
                                    "startDate",
                                    getTodayWithTimeTravel()
                                        .subtract(2, "months")
                                        .toDate(),
                                );
                                f.setValue(
                                    "endDate",
                                    getTodayWithTimeTravel().toDate(),
                                );
                            }}
                        >
                            {lastTwoMonths && (
                                <FontAwesomeIcon icon={faCheck} />
                            )}
                            {t("last_two_months")}
                        </Button>
                        <Button
                            variant="flat"
                            color={lastTwoWeeks ? "success" : "default"}
                            size="sm"
                            onPress={() => {
                                f.setValue(
                                    "startDate",
                                    getTodayWithTimeTravel()
                                        .subtract(2, "weeks")
                                        .toDate(),
                                );
                                f.setValue(
                                    "endDate",
                                    getTodayWithTimeTravel().toDate(),
                                );
                            }}
                        >
                            {lastTwoWeeks && <FontAwesomeIcon icon={faCheck} />}
                            {t("last_two_weeks")}
                        </Button>
                    </div>
                </div>
                <div className="flex flex-row justify-evenly  gap-2">
                    <DateInput
                        name="startDate"
                        label={t("start_date")}
                        size="sm"
                    />
                    <DateInput name="endDate" label={t("end_date")} size="sm" />
                </div>
                {fieldHasInsights && (
                    <SITrendline
                        fieldId={selectedField?.id?.toString()}
                        disableResize
                        dateRange={{
                            start: parseDate(
                                dayjs(startDate).format("YYYY-MM-DD"),
                            ),
                            end: parseDate(dayjs(endDate).format("YYYY-MM-DD")),
                        }}
                        selectedPairIds={[
                            "1",
                            "2",
                            "3",
                            "4",
                            "5",
                            "6",
                            "7",
                            "8",
                            "9",
                            "10",
                            "11",
                            "12",
                            "13",
                            "14",
                            "15",
                            "16",
                            "17",
                            "18",
                            "19",
                            "20",
                        ]}
                        cropStage={undefined}
                        disableMap
                    />
                )}
                {fieldHasIrrigation && (
                    <>
                        <Divider />
                        <div className="flex flex-row items-center justify-end">
                            <img
                                src={hydroFlowHorizontal}
                                alt="HydroFlow"
                                className="h-6 w-auto"
                            />
                        </div>
                        <SoilWaterContent
                            fieldId={selectedField?.id?.toString()}
                            dateRange={{
                                start: parseDate(
                                    dayjs(startDate).format("YYYY-MM-DD"),
                                ),
                                end: parseDate(
                                    dayjs(endDate).format("YYYY-MM-DD"),
                                ),
                            }}
                        />
                    </>
                )}
            </FormProvider>
            <Divider />
            {notificationsV2Enabled && (
                <StatusTimeline fieldId={selectedField?.id ?? undefined} />
            )}
        </div>
    );
}

export default SIPreview;
